<script setup lang="ts">
// The login page, bare (no shell): the ITC Web brand (the crest's place, until the
// asset exists: plan 1E B1), one primary "Log in with Discord", and why a login
// failed as a banner.
import { LoginError } from '@itc/shared';
import { Banner, Button } from '@itc/ui';
import { computed } from 'vue';
import { useRoute } from 'vue-router';

const route = useRoute();

const MESSAGES: Record<LoginError, string> = {
  not_member: "You need to be a member of the org's Discord server to use ITC Web.",
  no_role: "Your Discord account doesn't have the role ITC Web needs. Ask an officer if you think that's wrong.",
  banned: 'This account has been banned from ITC Web.',
  discord_unavailable: "Discord didn't answer. Try again in a minute.",
  cancelled: 'Login was cancelled.',
  expired: 'Your session has ended. Log in again.',
};

const error = computed(() => {
  const parsed = LoginError.safeParse(route.query['error']);
  return parsed.success ? MESSAGES[parsed.data] : null;
});

const loginHref = computed(() => {
  const next = typeof route.query['next'] === 'string' ? route.query['next'] : '/';
  return `/api/auth/login?next=${encodeURIComponent(next)}`;
});
</script>

<template>
  <main class="login">
    <div class="login__card">
      <div class="login__brand">ITC Web<small>Org console</small></div>
      <h1 class="login__title">Log in</h1>
      <p class="login__text">Log in with the Discord account you use in the org's server.</p>
      <Banner v-if="error" tone="crit">{{ error }}</Banner>
      <Button variant="primary" :href="loginHref" class="login__button">Log in with Discord</Button>
      <p class="login__note"><span class="itc-dot t-good" aria-hidden="true"></span>Members of the org's Discord server only</p>
    </div>
  </main>
</template>

<style scoped>
.login {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 24px 16px;
}
.login__card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 420px;
  max-width: 100%;
  padding: 28px 24px;
  background: var(--itc-panel);
  border: 1px solid var(--itc-line);
  border-radius: var(--itc-r-panel);
}
.login__brand {
  padding-bottom: 14px;
  border-bottom: 1px solid var(--itc-line);
  color: var(--itc-accent);
  font-family: var(--itc-font-display);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.14em;
  line-height: 1.1;
  text-align: center;
  text-transform: uppercase;
}
.login__brand small {
  display: block;
  margin-top: 4px;
  color: var(--itc-muted);
  font-size: 11px;
  letter-spacing: 0.16em;
}
.login__title {
  margin: 0;
  font-family: var(--itc-font-display);
  font-size: 30px;
  font-weight: 700;
  letter-spacing: 0.02em;
}
.login__text {
  margin: 0;
  color: var(--itc-muted);
}
.login__button {
  width: 100%;
}
.login__note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  color: var(--itc-muted);
  font-size: 13px;
}
</style>
